import { floorToEuro, formatEUR, type SavingsGoalResponse } from '@budget/shared';
import { type RefObject, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSavings } from '../api/hooks';
import { goalGlyph } from './glyphs';
import { GoalRing } from './GoalRing';
import { type MoveDirection, SavingsMoveModal } from './SavingsMoveModal';

const MONTH_YEAR = new Intl.DateTimeFormat('fr-FR', { month: 'short', year: 'numeric' });

function deadlineLabel(iso: string): string {
  // `T12:00` évite qu'un fuseau négatif fasse reculer la date d'un jour.
  return MONTH_YEAR.format(new Date(`${iso}T12:00:00`));
}

interface Props {
  /** Compte courant observé : source d'une mise de côté, destination d'un retrait. */
  accountId: string;
  /** Son solde, montré dans le schéma du mouvement. */
  accountBalanceCents: number;
  /** Son pictogramme, repris dans ce schéma. */
  accountIcon: string;
  /**
   * Sens du mouvement proposé, décidé par l'écran d'accueil : mettre de côté
   * quand le mois est en avance, renflouer quand il est en retard ou que le
   * solde est négatif.
   */
  direction: MoveDirection;
  /** Avance à placer, ou manque à combler. Toujours positif ou nul. */
  suggestedCents: number;
  anchorRef: RefObject<HTMLElement | null>;
}

/**
 * Résumé des pôles sur l'accueil, en petits multiples.
 *
 * Chaque anneau porte son propre objectif : on ne compare pas des parts entre
 * elles, on lit une progression à côté de son échéance.
 *
 * Toucher un anneau ouvre le mouvement qui a du sens ce jour-là — mettre de
 * côté quand il reste du gras, reprendre quand le compte manque d'air. Le
 * geste est le même, c'est l'état du compte qui décide du sens.
 */
export function SavingsSummary({
  accountId,
  accountBalanceCents,
  accountIcon,
  direction,
  suggestedCents,
  anchorRef,
}: Props) {
  const savings = useSavings();
  const [target, setTarget] = useState<SavingsGoalResponse | null>(null);

  const goals = savings.data?.goals ?? [];
  const isDeposit = direction === 'deposit';

  if (savings.isPending) return null;

  return (
    <section className="rounded-2xl border border-line bg-surface p-4">
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-medium">Objectifs d’épargne</h2>
        <Link to="/epargne" className="text-xs text-ink-secondary">
          Tout voir ›
        </Link>
      </div>

      {goals.length > 0 && (
        <p className={`mt-1 text-xs ${isDeposit ? 'text-success-text' : 'text-critical'}`}>
          <span aria-hidden="true">{isDeposit ? '▲' : '▼'}</span>{' '}
          {formatEUR(floorToEuro(Math.max(suggestedCents, 0)))}{' '}
          {isDeposit
            ? 'd’avance — touchez un objectif pour en mettre de côté.'
            : 'd’écart sur la trajectoire idéale. Équilibrer ?'}
        </p>
      )}

      {goals.length === 0 ? (
        <p className="mt-2 text-sm text-ink-secondary">
          Aucun pôle pour l’instant.{' '}
          <Link to="/epargne" className="underline">
            En créer un
          </Link>{' '}
          pour suivre un objectif daté.
        </p>
      ) : (
        <ul className="mt-4 grid grid-cols-3 gap-x-3 gap-y-5">
          {goals.map((goal) => (
            <li key={goal.id}>
              <button
                type="button"
                onClick={() => setTarget(goal)}
                aria-label={
                  isDeposit ? `Mettre de côté sur ${goal.name}` : `Retirer de ${goal.name}`
                }
                className="flex w-full flex-col items-center gap-1.5 rounded-xl py-1 active:bg-line"
              >
                <p className="w-full truncate text-center text-xs font-medium" title={goal.name}>
                  <span aria-hidden="true">{goalGlyph(goal)}</span> {goal.name}
                </p>

                <div className="w-full px-1">
                  <GoalRing progress={goal.progress} label={goal.name} />
                </div>

                <div className="text-center">
                  <p className="tabular text-xs">
                    {formatEUR(
                      goal.recurrence === 'none' ? goal.savedCents : goal.periodSavedCents,
                      { round: true },
                    )}
                  </p>
                  <p
                    className={`text-[11px] ${
                      goal.progress.status === 'behind' ? 'text-critical' : 'text-ink-muted'
                    }`}
                  >
                    {goal.progress.status === 'behind' && <span aria-hidden="true">▼ </span>}
                    {/* Pour un objectif récurrent, c'est l'échéance EN COURS qui
                        compte, pas la première jamais fixée. */}
                    {goal.period === null ? 'sans échéance' : deadlineLabel(goal.period.dueOn)}
                  </p>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {savings.data !== undefined && savings.data.unassignedCents !== 0 && (
        <p className="mt-4 border-t border-line pt-2 text-xs text-ink-muted">
          {formatEUR(savings.data.unassignedCents, { round: true })} non affectés à un pôle.
        </p>
      )}

      {target !== null && (
        <SavingsMoveModal
          goal={target}
          direction={direction}
          accountId={accountId}
          accountBalanceCents={accountBalanceCents}
          accountIcon={accountIcon}
          suggestedCents={suggestedCents}
          anchorRef={anchorRef}
          onClose={() => setTarget(null)}
        />
      )}
    </section>
  );
}
